import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Page, PageHeader, EmptyState, Modal, Field, inputCls, PrimaryButton,
} from "@/components/lims";
import {
  ASSIGNABLE_ROLES, ROLE_DESCRIPTIONS, canManageUsers, roleBadgeStyle, roleLabel,
  rolesForUser,
} from "@/lib/roles";
import { UsersThree, Plus, ShieldCheck, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";

const emptyInvite = { email: "", name: "", roles: ["analyst"], client_id: "" };

function RolePicker({ selected, onToggle, disabled = false, testIdPrefix }) {
  return (
    <fieldset className="flex flex-wrap gap-1.5" disabled={disabled}>
      <legend className="sr-only">Assigned roles</legend>
      {ASSIGNABLE_ROLES.map((role) => {
        const checked = selected.includes(role);
        return (
          <label
            key={role}
            className={`cursor-pointer rounded-full border px-2.5 py-1 text-xs transition-colors ${
              checked ? roleBadgeStyle(role) : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
            } ${disabled ? "cursor-wait opacity-60" : ""}`}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              data-testid={`${testIdPrefix}-${role}`}
              onChange={() => onToggle(role)}
            />
            {roleLabel(role)}
          </label>
        );
      })}
    </fieldset>
  );
}

export default function Team() {
  const { user, refresh } = useAuth();
  const canManage = canManageUsers(user);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyInvite);
  const [invitation, setInvitation] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    if (canManage) api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, [canManage]);

  const fail = (err) =>
    toast.error(formatApiError(err.response?.data?.detail) || err.message);

  const changeRoles = async (u, roles) => {
    if (roles.includes("client_user") && !u.client_id) {
      toast.error("Assign a client when creating a client portal member");
      load();
      return;
    }
    setSavingId(u.id);
    try {
      const clientId = roles.includes("client_user") ? u.client_id || null : null;
      await api.patch(`/users/${u.id}/role`, {
        role: roles[0], roles, client_id: clientId,
      });
      setUsers((prev) => prev.map((x) => (x.id === u.id
        ? { ...x, role: roles[0], roles, client_id: clientId }
        : x)));
      if (u.id === user?.id) await refresh?.();
      toast.success(`${u.name}'s roles were updated`);
    } catch (err) {
      fail(err);
      load();
    } finally {
      setSavingId(null);
    }
  };

  const toggleMemberRole = (u, role) => {
    const current = rolesForUser(u);
    if (current.includes(role)) {
      if (current.length === 1) {
        toast.error("Every member must have at least one role");
        return;
      }
      changeRoles(u, current.filter((assigned) => assigned !== role));
      return;
    }
    if (role === "client_user") {
      changeRoles(u, [role]);
      return;
    }
    changeRoles(u, current.includes("client_user") ? [role] : [...current, role]);
  };

  const toggleInviteRole = (role) => {
    const current = form.roles;
    let roles;
    if (current.includes(role)) {
      if (current.length === 1) return;
      roles = current.filter((assigned) => assigned !== role);
    } else if (role === "client_user") {
      roles = [role];
    } else {
      roles = current.includes("client_user") ? [role] : [...current, role];
    }
    setForm({ ...form, roles, client_id: roles.includes("client_user") ? form.client_id : "" });
  };

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/users", { ...form, role: form.roles[0] });
      setInvitation(data);
      setForm(emptyInvite);
      load();
    } catch (err) {
      fail(err);
    }
  };

  const setActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.id}/${active ? "activate" : "deactivate"}`);
      toast.success(active ? "Member reactivated" : "Member deactivated");
      load();
    } catch (err) {
      fail(err);
    }
  };

  const resetMfa = async (u) => {
    try {
      await api.post(`/users/${u.id}/mfa-reset`);
      toast.success(`${u.name} will set up a new authenticator at next sign-in`);
      load();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Page>
      <PageHeader
        title="Team"
        subtitle="Assign one or more roles to match how your laboratory operates"
        action={canManage && (
          <PrimaryButton data-testid="invite-user-button" onClick={() => setOpen(true)}>
            <Plus size={18} /> Add member
          </PrimaryButton>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {ASSIGNABLE_ROLES.map((r) => (
          <div key={r} className="bg-white rounded-lg border border-slate-200 p-4">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleBadgeStyle(r)}`}>
              {roleLabel(r)}
            </span>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{ROLE_DESCRIPTIONS[r]}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
          </div>
        ) : users.length === 0 ? (
          <EmptyState icon={UsersThree} title="No members"
            subtitle="Add a member to invite them to this laboratory" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Email</th>
                <th className="px-5 py-3 font-semibold">Roles</th>
                <th className="px-5 py-3 font-semibold">Client scope</th>
                <th className="px-5 py-3 font-semibold">2FA</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`team-row-${u.email}`}
                  className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${
                    u.membership_active === false ? "opacity-50" : ""}`}>
                  <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                      {u.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    {u.name}
                    {u.id === user?.id && <span className="text-xs text-slate-400">(you)</span>}
                    {u.membership_active === false &&
                      <span className="text-xs text-slate-400">(deactivated)</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-500">{u.email}</td>
                  <td className="px-5 py-3">
                    {canManage ? (
                      <RolePicker
                        selected={rolesForUser(u)}
                        disabled={savingId === u.id}
                        onToggle={(role) => toggleMemberRole(u, role)}
                        testIdPrefix={`role-checkbox-${u.email}`}
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {rolesForUser(u).map((role) => (
                          <span key={role} className={`text-xs font-medium px-2.5 py-1 rounded-full ${roleBadgeStyle(role)}`}>
                            {roleLabel(role)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">{rolesForUser(u).includes("client_user") ? (clients.find((client) => client.id === u.client_id)?.legal_name || "Not assigned") : "—"}</td>
                  <td className="px-5 py-3">
                    {u.mfa_enabled
                      ? <span className="text-xs text-status-ok">Enrolled</span>
                      : <span className="text-xs text-status-warn">Pending</span>}
                  </td>
                  <td className="px-5 py-3 text-right space-x-2 whitespace-nowrap">
                    {canManage && u.id !== user?.id && (
                      <>
                        <button onClick={() => resetMfa(u)} title="Reset authenticator"
                          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                          <ShieldCheck size={15} /> Reset 2FA
                        </button>
                        {u.membership_active === false ? (
                          <button onClick={() => setActive(u, true)}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-status-ok">
                            <ArrowCounterClockwise size={15} /> Reactivate
                          </button>
                        ) : (
                          <button onClick={() => setActive(u, false)}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-status-warn">
                            <Prohibit size={15} /> Deactivate
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        Members are deactivated, never deleted. Access to this laboratory ends
        immediately and their history is retained; sessions shared with another
        laboratory are not disrupted.
      </p>

      <Modal open={open} onClose={() => { setOpen(false); setInvitation(null); }}
        title="Add member" testid="invite-modal">
        {invitation ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Account created for <strong>{invitation.email}</strong>.
            </p>
            {invitation.invitation_token ? (
              <>
                <p className="text-sm text-slate-600">
                  Send them this activation link. It can be used once and expires in{" "}
                  {invitation.invitation_expires_in_hours} hours. You are not given
                  their password. They choose it themselves.
                </p>
                <code className="block break-all rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs font-mono text-slate-700">
                  {`${window.location.origin}/accept-invitation?token=${invitation.invitation_token}`}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      `${window.location.origin}/accept-invitation?token=${invitation.invitation_token}`
                    );
                    toast.success("Link copied");
                  }}
                  className="text-xs text-slate-500 hover:text-slate-900"
                >
                  Copy link
                </button>
              </>
            ) : invitation.invitation_delivery === "email" ? (
              <p className="text-sm text-slate-600">
                An activation link was e-mailed to them. It can be used once and
                expires in {invitation.invitation_expires_in_hours} hours. You are
                not given their password. They choose it themselves.
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                They already had an account, so they were added to this laboratory
                directly and can sign in with their existing credentials.
              </p>
            )}
            <PrimaryButton className="w-full justify-center"
              onClick={() => { setOpen(false); setInvitation(null); }}>
              Done
            </PrimaryButton>
          </div>
        ) : (
          <form onSubmit={invite} className="space-y-4">
            <Field label="Full name">
              <input className={inputCls} required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Email">
              <input className={inputCls} type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Roles">
              <RolePicker
                selected={form.roles}
                onToggle={toggleInviteRole}
                testIdPrefix="invite-role"
              />
              <p className="mt-2 text-xs text-slate-400">
                {form.roles.map((role) => ROLE_DESCRIPTIONS[role]).join(" · ")}
              </p>
            </Field>
            {form.roles.includes("client_user") && <Field label="Client data scope">
              <select className={inputCls} required value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Select client…</option>
                {clients.map((client) => <option key={client.id} value={client.id}>{client.legal_name}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-400">The portal API will only return samples belonging to this client.</p>
            </Field>}
            <PrimaryButton type="submit" className="w-full justify-center">
              Create account
            </PrimaryButton>
          </form>
        )}
      </Modal>
    </Page>
  );
}
